<script lang="ts">
    import ColorThumbs from '@tile/components/color-thumbs/ColorThumbs.svelte';
    import EekLabel from '@tile/components/eek/EekLabel.svelte';
    import JsonLd from '@tile/components/json-ld/JsonLd.svelte';
    import OttoUp from '@tile/components/otto-up/OttoUp.svelte';
    import ProductImageCarousel from '@tile/components/product-image-carousel/ProductImageCarousel.svelte';
    import ProductImage from '@tile/components/product-image/ProductImage.svelte';
    import type { ImageAspectRatio } from '@tile/components/product-image/types.ts';
    import ProductLink from '@tile/components/product-title/ProductLink.svelte';
    import ShortTitle from '@tile/components/product-title/ShortTitle.svelte';
    import RadioEquipmentRegulation from '@tile/components/radio-equipment-regulation/RadioEquipmentRegulation.svelte';
    import SponsoredHint from '@tile/components/sponsored-hint/SponsoredHint.svelte';
    import SustainabilityBadges from '@tile/components/sustainability-badges/SustainabilityBadges.svelte';
    import { injectWiring } from '@tilelist/application/wiring/TileListWiringContext.svelte.ts';
    import type { AdditionalImagesType, ExperimentGroups } from '@tilelist/domain/experiments/Experiments.ts';
    import PriceDispatcher from '@tilelist/layouts/experiments/price-dispatcher/PriceDispatcher.svelte';
    import type { FashionLayout } from '@tilelist/layouts/fashion-layout/FashionLayout.ts';
    import type { TileLayoutProps } from '@tilelist/layouts/shared/TileLayoutProps.ts';

    type Props = TileLayoutProps & {
        layout: FashionLayout;
        aspectRatio?: ImageAspectRatio;
        bestVariationBenefit?: string;
    };

    let {
        layout,
        colors,
        sponsored,
        advertiserLegalName,
        funderLegalName,
        imageLoadingOptions,
        rule,
        aspectRatio = 'portrait',
        onColorChange,
        onRedirectToPdp,
        element = $bindable(null),
        bestVariationBenefit,
    }: Props = $props();

    const wiring = injectWiring(['trackingService']);
    const onex = wiring.onex;

    const forceIndex = $derived(onex.isToggleActive('REPTILE_1495_FORCE_INDEX'));
    const { variation, title, link: rawLink } = $derived(layout);
    const link = $derived(forceIndex ? { ...rawLink, index: true } : rawLink);

    const additionalImagesMapping: Record<ExperimentGroups['ADDITIONAL_IMAGES'], AdditionalImagesType> = {
        E3799A: 'Single',
        E3799B: 'CarouselV1',
        E3799C: 'CarouselV2',
    };

    const additionalImagesGroup = $derived(onex.matchGroup('ADDITIONAL_IMAGES', additionalImagesMapping));
</script>

<div data-jarvis="reptile-tile-container" data-variation-id={variation.variationId} bind:this={element}>
    {#if additionalImagesGroup === 'CarouselV1' || additionalImagesGroup === 'CarouselV2'}
        <ProductImageCarousel
            images={layout.images}
            fallbackImage={'image' in variation
                ? variation.image
                : { id: '', alt: '', fallback: '', jpeg: '', webp: '' }}
            variationId={variation.variationId}
            detailPageLink={variation.detailPageLink}
            brand={variation.brand || ''}
            title={variation.title?.withoutBrand || variation.title?.full || ''}
            {imageLoadingOptions}
            onClick={onRedirectToPdp}
            onImageChange={() => {
                wiring.trackingService?.trackInteraction('product_image_change');
            }}
            deal={variation.deal}
            socialProof={variation.socialProof}
            {aspectRatio}
            {rule}
            size={additionalImagesGroup === 'CarouselV1' ? '100' : '50'}
            hidePaginationDots={additionalImagesGroup === 'CarouselV1'}
        />
    {:else}
        <ProductImage
            variationId={variation.variationId}
            image={'image' in variation ? variation.image : { id: '', alt: '', fallback: '', jpeg: '', webp: '' }}
            socialProof={variation.socialProof}
            detailPageLink={variation.detailPageLink}
            onClick={onRedirectToPdp}
            deal={variation.deal}
            {imageLoadingOptions}
            {aspectRatio}
            {rule}
        />
    {/if}
    <div class="reptile-tile__content">
        <header class="reptile-tile__header">
            <SponsoredHint
                sponsored={sponsored ?? false}
                funderLegalName={funderLegalName ?? ''}
                advertiserLegalName={advertiserLegalName ?? ''}
            />
            <ProductLink {link} onClick={onRedirectToPdp}>
                <ShortTitle {title} />
            </ProductLink>
        </header>
        <EekLabel labels={variation?.energyLabels || []} />
        <RadioEquipmentRegulation radioEquipmentRegulation={variation.radioEquipmentRegulation} />
        <PriceDispatcher
            variationId={variation.variationId}
            saleTags={variation?.saleTags}
            price={variation.price}
            dealHighlight={variation.deal?.highlight}
            {rule}
            {bestVariationBenefit}
        />
        <ColorThumbs
            {colors}
            currentVariationId={variation.variationId}
            detailPageLink={variation.detailPageLink}
            productLinkTitle={variation.title?.full || ''}
            onClickSurplusColors={onRedirectToPdp}
            {onColorChange}
        />
        <SustainabilityBadges badges={variation.sustainabilityBadges} />
        <OttoUp show={variation.ottoUp} />
    </div>
    <JsonLd {variation} />
</div>

<style lang="scss">
    @use '@otto-ec/design-tokens/tokens';
    @use '@otto-ec/otto-components-utils/scss/mixins';

    .reptile-tile {
        &__content,
        &__header {
            display: flex;
            flex-direction: column;
        }

        &__content {
            padding: tokens.$oc-semantic-spacing-100 tokens.$oc-semantic-spacing-75;
            row-gap: tokens.$oc-semantic-spacing-50;

            @include mixins.breakpoint-from-l() {
                padding: tokens.$oc-semantic-spacing-100;
            }
        }

        &__header {
            row-gap: tokens.$oc-semantic-spacing-default-between-blocks;
        }
    }
</style>
